import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { UIProvider } from "@/context/UIContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { CartProvider } from "@/context/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import Home from "@/pages/Home";
import PartnerProfile from "@/pages/PartnerProfile";
import ReferralRedirect from "@/pages/ReferralRedirect";
import PartnerMarketing from "@/pages/PartnerMarketing";
import PartnerOnboarding from "@/pages/PartnerOnboarding";
import PartnerDashboard from "@/pages/PartnerDashboard";
import PartnerProfileEditor from "@/pages/PartnerProfileEditor";
import PartnerPublish from "@/pages/PartnerPublish";
import PartnerAnalytics from "@/pages/PartnerAnalytics";
import PartnerDirectory from "@/pages/PartnerDirectory";
import Onboarding from "@/pages/Onboarding";
import MyHomeRange from "@/pages/MyHomeRange";
import MyHomeRangeData from "@/pages/MyHomeRangeData";
import HuntPassport from "@/pages/HuntPassport";
import HuntRecord from "@/pages/HuntRecord";
import Applications from "@/pages/Applications";
import TrophyRoom from "@/pages/TrophyRoom";
import YearInReview from "@/pages/YearInReview";
import HuntCardPage from "@/pages/HuntCardPage";
import HuntExplorer from "@/pages/HuntExplorer";
import StatesDirectory from "@/pages/StatesDirectory";
import StatePage from "@/pages/StatePage";
import SpeciesPage from "@/pages/SpeciesPage";
import Planner from "@/pages/Planner";
import Deadlines from "@/pages/Deadlines";
import MapPreview from "@/pages/MapPreview";
import TerrainAnalysis from "@/pages/TerrainAnalysis";
import HuntDetail from "@/pages/HuntDetail";
import SharedHunt from "@/pages/SharedHunt";
import HuntAssistant from "@/pages/HuntAssistant";
import Conditions from "@/pages/Conditions";
import Business from "@/pages/Business";
import Marketplace from "@/pages/Marketplace";
import GearMarketplace from "@/pages/GearMarketplace";
import FieldNotes from "@/pages/FieldNotes";
import Archive from "@/pages/Archive";
import ProfilePage from "@/pages/ProfilePage";
import NotificationsPage from "@/pages/NotificationsPage";
import { FirearmsResearch, FirearmDetail } from "@/pages/FirearmsResearch";
import Outfitters from "@/pages/Outfitters";
import Recipes from "@/pages/Recipes";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import RecipeDetail from "@/pages/RecipeDetail";
import AskAI from "@/pages/AskAI";
import Account from "@/pages/Account";
import SearchPage from "@/pages/SearchPage";
import AdminFirearms from "@/pages/admin/AdminFirearms";
import Storefront from "@/pages/Storefront";
import AdminMarketplace from "@/pages/AdminMarketplace";
import PaymentStatus from "@/pages/PaymentStatus";
import Orders from "@/pages/Orders";
import Wishlist from "@/pages/Wishlist";
import Advertise from "@/pages/Advertise";
import AdvertiserDashboard from "@/pages/AdvertiserDashboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import { CookieConsent } from "@/components/CookieConsent";
import { GlobalOverlays } from "@/components/GlobalOverlays";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLogin from "@/pages/admin/AdminLogin";
import ResetPassword from "@/pages/admin/ResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSales from "@/pages/admin/AdminSales";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import AdminGuides from "@/pages/admin/AdminGuides";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminMarketplaceManager from "@/pages/admin/AdminMarketplaceManager";
import AdminHunts from "@/pages/admin/AdminHunts";
import AdminContent from "@/pages/admin/AdminContent";
import AdminFilms from "@/pages/admin/AdminFilms";
import AdminWebsite from "@/pages/admin/AdminWebsite";
import AdminPages from "@/pages/admin/AdminPages";
import AdminPageEditor from "@/pages/admin/AdminPageEditor";
import AdminNavigation from "@/pages/admin/AdminNavigation";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminArchive from "@/pages/admin/AdminArchive";
import AdminMerch from "@/pages/admin/AdminMerch";
import AdminCommerceProducts from "@/pages/admin/AdminCommerceProducts";
import AdminCommerceProductEditor from "@/pages/admin/AdminCommerceProductEditor";
import AdminCommerceCategories from "@/pages/admin/AdminCommerceCategories";
import AdminCommerceDiscounts from "@/pages/admin/AdminCommerceDiscounts";
import AdminCommerceOrders from "@/pages/admin/AdminCommerceOrders";
import AdminCommerceShipping from "@/pages/admin/AdminCommerceShipping";
import AdminCommerceReviews from "@/pages/admin/AdminCommerceReviews";
import AdminCommerceReturns from "@/pages/admin/AdminCommerceReturns";
import Returns from "@/pages/Returns";
import CmsPage from "@/pages/CmsPage";
import AdminAdvertising from "@/pages/admin/AdminAdvertising";
import AdminStates from "@/pages/admin/AdminStates";
import AdminAI from "@/pages/admin/AdminAI";
import AdminDataHealth from "@/pages/admin/AdminDataHealth";
import AdminReviewQueue from "@/pages/admin/AdminReviewQueue";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";

function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <SiteContentProvider>
        <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<StatesDirectory />} />
            <Route path="/explore/hub" element={<HuntExplorer />} />
            <Route path="/states/:state" element={<StatePage />} />
            <Route path="/states/:state/species/:name" element={<SpeciesPage />} />
            <Route path="/planner" element={<Planner />} />
            <Route path="/deadlines" element={<Deadlines />} />
            <Route path="/map-preview" element={<MapPreview />} />
            <Route path="/terrain-analysis" element={<TerrainAnalysis />} />
            <Route path="/gear" element={<GearMarketplace />} />
            <Route path="/field-notes" element={<FieldNotes />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/hunter/:id" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/firearms" element={<FirearmsResearch />} />
            <Route path="/firearms/:id" element={<FirearmDetail />} />
            <Route path="/outfitters" element={<Outfitters />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:slug" element={<ProductDetail />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/merch" element={<Navigate to="/shop" replace />} />
            <Route path="/recipes/:id" element={<RecipeDetail />} />
            <Route path="/ai" element={<AskAI />} />
            <Route path="/account" element={<Account />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/my-homerange" element={<MyHomeRange />} />
            <Route path="/my-homerange/data" element={<MyHomeRangeData />} />
            <Route path="/passport" element={<HuntPassport />} />
            <Route path="/hunts/:huntId" element={<HuntRecord />} />
            <Route path="/applications" element={<Applications />} />
            <Route path="/trophy-room" element={<TrophyRoom />} />
            <Route path="/year-in-review" element={<YearInReview />} />
            <Route path="/hunt-card/:cardId" element={<HuntCardPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/hunt/:id" element={<HuntDetail />} />
            <Route path="/shared-hunt/:shareId" element={<SharedHunt />} />
            <Route path="/hunt-assistant" element={<HuntAssistant />} />
            <Route path="/conditions/:state" element={<Conditions />} />
            <Route path="/p/:slug" element={<CmsPage />} />
            <Route path="/business" element={<Business />} />
            <Route path="/advertise" element={<Advertise />} />
            <Route path="/advertising/dashboard" element={<AdvertiserDashboard />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/store/:id" element={<Storefront />} />
            {/* Partner Platform (Phase 1) */}
            <Route path="/brands/:slug" element={<PartnerProfile prefix="brands" />} />
            <Route path="/outfitters/:slug" element={<PartnerProfile prefix="outfitters" />} />
            <Route path="/guides/:slug" element={<PartnerProfile prefix="guides" />} />
            <Route path="/r/:code" element={<ReferralRedirect />} />
            <Route path="/partner/marketing" element={<PartnerMarketing />} />
            <Route path="/partner/onboarding" element={<PartnerOnboarding />} />
            <Route path="/partner/dashboard" element={<PartnerDashboard />} />
            <Route path="/partner/edit" element={<PartnerProfileEditor />} />
            <Route path="/partner/publish" element={<PartnerPublish />} />
            <Route path="/partner/analytics" element={<PartnerAnalytics />} />
            <Route path="/partners" element={<PartnerDirectory />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/payment/success" element={<PaymentStatus />} />
            <Route path="/payment/cancel" element={<PaymentStatus />} />
            {/* Admin portal */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminGuard />}>
              <Route index element={<AdminDashboard />} />
              <Route path="sales" element={<AdminSales />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="advertising" element={<AdminAdvertising />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="approvals" element={<AdminApprovals />} />
              <Route path="guides" element={<AdminGuides />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="states" element={<AdminStates />} />
              <Route path="ai" element={<AdminAI />} />
              <Route path="data-health" element={<AdminDataHealth />} />
              <Route path="review-queue" element={<AdminReviewQueue />} />
              <Route path="marketplace" element={<AdminMarketplaceManager />} />
              <Route path="hunts" element={<AdminHunts />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="films" element={<AdminFilms />} />
              <Route path="firearms" element={<AdminFirearms />} />
              <Route path="website" element={<AdminWebsite />} />
              <Route path="navigation" element={<AdminNavigation />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="pages/:id" element={<AdminPageEditor />} />
              <Route path="analytics" element={<AdminPlaceholder />} />
              <Route path="reports" element={<AdminPlaceholder />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="merch" element={<AdminMerch />} />
              <Route path="commerce/products" element={<AdminCommerceProducts />} />
              <Route path="commerce/products/:id" element={<AdminCommerceProductEditor />} />
              <Route path="commerce/categories" element={<AdminCommerceCategories />} />
              <Route path="commerce/discounts" element={<AdminCommerceDiscounts />} />
              <Route path="commerce/orders" element={<AdminCommerceOrders />} />
              <Route path="commerce/shipping" element={<AdminCommerceShipping />} />
              <Route path="commerce/reviews" element={<AdminCommerceReviews />} />
              <Route path="commerce/returns" element={<AdminCommerceReturns />} />
              <Route path="archive" element={<AdminArchive />} />
              <Route path="support" element={<AdminPlaceholder />} />
              <Route path="notifications" element={<AdminPlaceholder />} />
            </Route>
            <Route path="/admin/legacy" element={<AdminMarketplace />} />
          </Routes>
          <GlobalOverlays />
          <CartDrawer />
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        </CartProvider>
        </SiteContentProvider>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;
